import type { ReportsResponse } from '@hbs-us/contracts';
import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { ReportsSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE reports endpoint (the strangler-fig + coarse
 * -endpoint proof, CARVE-07). One query key per `{range,start,end}` — changing
 * the range triggers exactly one refetch of `GET /api/v1/reports?range=…`. The
 * SERVER computes the whole report (`buildAdvancedReports`); the SPA only renders
 * the returned `ReportsResponse` (no client re-aggregation, no new N+1 —
 * threat T-20-14).
 *
 * The request goes through `apiFetch` (the only fetch path — `credentials:'include'`
 * + `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is
 * derived server-side from the auth context; the SPA never sends a tenant id
 * param (threat T-20-13).
 */

/** Build the stable query key for a filter (one cache entry per window). */
export function reportsQueryKey(search: ReportsSearch): readonly unknown[] {
  return ['reports', search.range, search.start ?? null, search.end ?? null] as const;
}

/** Build the coarse-endpoint request path with the active filter. */
export function reportsRequestPath(search: ReportsSearch): string {
  const params = new URLSearchParams();
  params.set('range', search.range);
  if (search.range === 'custom') {
    if (search.start) params.set('start', search.start);
    if (search.end) params.set('end', search.end);
  }
  return `/api/v1/reports?${params.toString()}`;
}

export function useReportsQuery(search: ReportsSearch) {
  return useQuery<ReportsResponse>({
    queryKey: reportsQueryKey(search),
    queryFn: () => apiFetchJson<ReportsResponse>(reportsRequestPath(search)),
  });
}
